// Tiny formatting helpers with no external deps to keep the bundle small.

export function formatDateTime(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  })
}

export function formatDate(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString()
}

// ---------------------------------------------------------------------------
// Papua New Guinea time (Pacific/Port_Moresby).
//
// Appointments are a PNG wall-clock concept: a slot at "3:00 PM" means 3pm
// in Port Moresby, whatever timezone the patient's or doctor's device is set
// to. The booking UI therefore formats AND constructs slot times in PNG time,
// not the browser's local timezone. PNG is a FIXED UTC+10 with NO daylight
// saving, so the +10:00 offset is safe to hard-code (no DST edge cases).
// ---------------------------------------------------------------------------

export const PNG_TZ = 'Pacific/Port_Moresby'
const PNG_OFFSET = '+10:00' // fixed; PNG has never observed DST

/** "03:00 PM" in PNG time, from an ISO/UTC instant. */
export function pngTime(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleTimeString('en-AU', {
    timeZone: PNG_TZ, hour: '2-digit', minute: '2-digit',
  })
}

/** "Fri, 13 Jun 2026, 03:00 PM" in PNG time — appointment timestamp. */
export function pngDateTime(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('en-AU', {
    timeZone: PNG_TZ, weekday: 'short', day: '2-digit', month: 'short',
    year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

/** "YYYY-MM-DD" calendar day in PNG for a given Date (default: now). */
export function pngDayKey(date = new Date()) {
  // en-CA renders ISO-style YYYY-MM-DD.
  return new Intl.DateTimeFormat('en-CA', { timeZone: PNG_TZ }).format(date)
}

/** Add N days to a YYYY-MM-DD key (pure calendar arithmetic, tz-agnostic). */
export function addDays(yyyymmdd, n) {
  const [y, mo, d] = yyyymmdd.split('-').map(Number)
  const dt = new Date(Date.UTC(y, mo - 1, d))
  dt.setUTCDate(dt.getUTCDate() + n)
  return dt.toISOString().slice(0, 10)
}

/** "Fri, 13 Jun 2026" label for a PNG calendar-day key. */
export function pngDayLabel(yyyymmdd) {
  if (!yyyymmdd) return ''
  // Anchor at PNG noon so no timezone rounding can shift the date.
  const d = new Date(`${yyyymmdd}T12:00:00${PNG_OFFSET}`)
  if (Number.isNaN(d.getTime())) return yyyymmdd
  return d.toLocaleDateString('en-AU', {
    timeZone: PNG_TZ, weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  })
}

/**
 * A PNG calendar day (YYYY-MM-DD) → the UTC ISO bounds that cover it.
 * Half-open [start, end): start = 00:00 PNG that day, end = 00:00 PNG next
 * day, which matches the backend's `slot_at__gte=from`, `slot_at__lt=to`.
 */
export function pngDayToUtcRange(yyyymmdd) {
  const start = new Date(`${yyyymmdd}T00:00:00${PNG_OFFSET}`).toISOString()
  const end = new Date(`${addDays(yyyymmdd, 1)}T00:00:00${PNG_OFFSET}`).toISOString()
  return { start, end }
}

/** PNG wall-clock (day + hour + minute) → the UTC ISO instant to store. */
export function pngDateTimeToUtcIso(yyyymmdd, hh, mm) {
  const t = `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`
  return new Date(`${yyyymmdd}T${t}:00${PNG_OFFSET}`).toISOString()
}

/** "HH:MM" (24-hour) in PNG, for matching a slot to an availability-grid cell. */
export function pngHHMM(iso) {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: PNG_TZ, hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(d)
}

// ---------------------------------------------------------------------------
// Patient date-of-birth / age — one shared implementation so every surface
// (CNE editor, screening, provider queues) and the printed documents read
// identically: "DOB 14 May 1991 · 34 yrs".
// ---------------------------------------------------------------------------

const _MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** Format a DOB as "14 May 1991" (matches the d M Y style used in PDFs). */
export function formatDob(value) {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return `${d.getDate()} ${_MONTHS[d.getMonth()]} ${d.getFullYear()}`
}

/** Whole-year age from a DOB, or null when unknown/invalid. */
export function ageYears(value) {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return null
  const now = new Date()
  let a = now.getFullYear() - d.getFullYear()
  const m = now.getMonth() - d.getMonth()
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a -= 1
  return a
}

/** Combined "DOB 14 May 1991 · 34 yrs" (or "" when no DOB). */
export function dobAge(value) {
  const dob = formatDob(value)
  if (!dob) return ''
  const a = ageYears(value)
  return a != null ? `DOB ${dob} · ${a} yrs` : `DOB ${dob}`
}

// PNG statutory retirement thresholds — mirrors backend
// documents/services/collectors.py. 65 = statutory; 60 = earliest age at
// which a doctor may exercise early medical retirement.
export const PNG_RETIREMENT_AGE = 65
export const PNG_EARLY_MEDICAL_RETIREMENT_AGE = 60

/**
 * Live advisory for the fitness assessment, given the patient DOB and the
 * doctor's "significant medical condition" flag. Returns null when DOB is
 * unknown, else { tone: 'warn'|'muted', text }.
 */
export function retirementNote(dob, significantCondition) {
  const a = ageYears(dob)
  if (a == null) return null
  if (a >= PNG_RETIREMENT_AGE) {
    return { tone: 'warn',
      text: `Age ${a} — at or over the statutory retirement age (${PNG_RETIREMENT_AGE}).` }
  }
  if (a >= PNG_EARLY_MEDICAL_RETIREMENT_AGE) {
    return significantCondition
      ? { tone: 'warn',
          text: `Age ${a} with a significant medical condition — early medical retirement may be exercised at your discretion (statutory age ${PNG_RETIREMENT_AGE}).` }
      : { tone: 'muted',
          text: `Age ${a} — early medical retirement is available from ${PNG_EARLY_MEDICAL_RETIREMENT_AGE} only where a significant medical condition is present.` }
  }
  return { tone: 'muted',
    text: `Age ${a} — below the early medical-retirement window (${PNG_EARLY_MEDICAL_RETIREMENT_AGE}+).` }
}

export function cx(...parts) {
  return parts.filter(Boolean).join(' ')
}

export function firstError(err, fallback = 'Something went wrong') {
  // Form-level validation errors are commonly raised as plain strings —
  // surface them verbatim instead of falling through to the generic
  // network-error fallback.
  if (typeof err === 'string') return err
  const r = err?.response?.data
  if (!r) return err?.message || fallback
  if (typeof r === 'string') return r
  // Phase 1 introduced a normalised envelope: { error: { code, message, details } }
  if (r.error && typeof r.error === 'object') {
    // The wrapped error envelope is { code, message, details }. The
    // message is often a generic header like "Validation failed." while
    // the actionable text lives in `details`. Surface the most specific
    // string we can find.
    const det = r.error.details
    if (det && typeof det === 'object' && !Array.isArray(det)) {
      const firstField = Object.keys(det)[0]
      const val = firstField && det[firstField]
      if (Array.isArray(val) && val.length) {
        return firstField === 'non_field_errors'
          ? String(val[0])
          : `${firstField}: ${val[0]}`
      }
      if (typeof val === 'string') {
        return firstField === 'non_field_errors' ? val : `${firstField}: ${val}`
      }
    }
    if (Array.isArray(det) && det.length && typeof det[0] === 'string') return det[0]
    if (r.error.message) return r.error.message
  }
  if (r.detail) return r.detail
  if (r.non_field_errors?.[0]) return r.non_field_errors[0]
  if (Array.isArray(r)) return r[0]
  const firstField = Object.keys(r)[0]
  const val = firstField && r[firstField]
  if (Array.isArray(val)) return `${firstField}: ${val[0]}`
  if (typeof val === 'string') return `${firstField}: ${val}`
  return fallback
}

// Extract the X-Request-ID associated with an error (or axios response).
// The api client attaches err.requestId in its response interceptor.
// We also check response.headers as a defensive fallback for callers that
// bypass the shared axios instance.
export function requestIdOf(err) {
  if (!err) return null
  if (err.requestId) return err.requestId
  const h = err.response?.headers
  if (h) return h['x-request-id'] || h['X-Request-ID'] || null
  return null
}

// Duplicate-patient guard (task #24, 2026-07-23). Both patient-registration
// endpoints (public /register-patient/ and doctor-driven POST /api/patients/)
// return HTTP 409 with error.code === 'likely_duplicate_patient' when the
// backend detects an existing PatientProfile that likely represents the same
// person. The FE surfaces a modal listing candidates so the user can pick
// "use existing" or "register anyway" (which resubmits with force_create=true).
//
// Kept as a shared helper here (not inlined at each call site) so a typo in
// the `code` string can't silently defeat the guard.
export function isLikelyDuplicatePatientError(err) {
  return (
    err?.response?.status === 409 &&
    err?.response?.data?.error?.code === 'likely_duplicate_patient'
  )
}

export function getDuplicatePatientCandidates(err) {
  if (!isLikelyDuplicatePatientError(err)) return []
  const cands = err.response.data.error.details?.candidates
  return Array.isArray(cands) ? cands : []
}

// Normalize paginated DRF responses and raw arrays into a plain array.
export function unwrapList(data) {
  if (Array.isArray(data)) return data
  if (data && Array.isArray(data.results)) return data.results
  return []
}

export function statusColor(status) {
  switch ((status || '').toLowerCase()) {
    case 'draft': return 'text-amber-600 bg-amber-50 border-amber-200'
    case 'submitted': return 'text-emerald-700 bg-emerald-50 border-emerald-200'
    case 'amended': return 'text-sky-700 bg-sky-50 border-sky-200'
    case 'pending':
    case 'routed': return 'text-slate-600 bg-slate-50 border-slate-200'
    case 'accepted':
    case 'in_progress': return 'text-indigo-700 bg-indigo-50 border-indigo-200'
    case 'dispensed':
    case 'completed': return 'text-emerald-700 bg-emerald-50 border-emerald-200'
    case 'cancelled':
    case 'rejected': return 'text-rose-700 bg-rose-50 border-rose-200'
    default: return 'text-slate-600 bg-slate-50 border-slate-200'
  }
}
